export interface APIKeyEntry {
  id: string;
  provider: 'groq' | 'openrouter' | 'gemini' | 'firecrawl' | 'steel';
  name: string;
  key: string;
  status: 'ACTIVE' | 'RATE_LIMITED' | 'EXHAUSTED' | 'DISABLED';
  usageCount: number;
  lastUsedAt?: string;
  dailyQuota?: number;
}

export interface BriefingData {
  companyName: string;
  segment: string;
  cityState: string;
  instagramHandle?: string;
  hasWebsite: boolean;
  mainGoal: string;
  whatsapp: string;
}

export interface MarketIntelligenceReport {
  companyName: string;
  segment: string;
  cityState: string;
  scoreAudit: number; // 0 to 100
  marketReachPotential: string;
  estimatedMonthlyLeads: number;
  googleRankOpportunity: string;
  commissionSavingsEstimate: string;
  maturityPhase: 'Início Digital' | 'Crescimento Acelerado' | 'Dominância de Mercado';
  recommendedPackage: 'DO OFF AO ON' | 'Growth 360°' | 'Enterprise';
  generatedAt: string;
}

const STORAGE_KEY = 'youmachine_ai_orchestrator_keys';

export const getStoredKeys = (): APIKeyEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefaultKeys();
    return JSON.parse(raw);
  } catch (e) {
    return getDefaultKeys();
  }
};

export const saveKeys = (keys: APIKeyEntry[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch (e) {
    console.error('Erro ao salvar chaves API:', e);
  }
};

export const getDefaultKeys = (): APIKeyEntry[] => [
  { id: 'key_groq_01', provider: 'groq', name: 'Groq Free Tier #1', key: 'gsk_demo_groq_key_9921', status: 'ACTIVE', usageCount: 142, dailyQuota: 14400 },
  { id: 'key_groq_02', provider: 'groq', name: 'Groq Free Tier #2', key: 'gsk_demo_groq_key_3310', status: 'ACTIVE', usageCount: 45, dailyQuota: 14400 },
  { id: 'key_openrouter_01', provider: 'openrouter', name: 'OpenRouter Primary Pool', key: 'sk-or-v1-demo-key-1092', status: 'ACTIVE', usageCount: 88, dailyQuota: 50000 },
  { id: 'key_gemini_01', provider: 'gemini', name: 'Google Gemini Flash Key', key: 'AIzaSyDemoGeminiKey_771', status: 'ACTIVE', usageCount: 230, dailyQuota: 1500 },
  { id: 'key_firecrawl_01', provider: 'firecrawl', name: 'Firecrawl Scraper Key', key: 'fc-demo-scraper-9921', status: 'ACTIVE', usageCount: 12, dailyQuota: 500 }
];

export const getNextActiveKey = (provider: APIKeyEntry['provider']): APIKeyEntry | null => {
  const keys = getStoredKeys();
  const available = keys.filter(k => k.provider === provider && k.status === 'ACTIVE');
  if (available.length === 0) return null;
  
  // Sort by usage to balance load
  available.sort((a, b) => a.usageCount - b.usageCount);
  const selected = available[0];

  // Increment usage count
  selected.usageCount += 1;
  selected.lastUsedAt = new Date().toISOString();
  saveKeys(keys);

  return selected;
};

export const generateMarketReport = (briefing: BriefingData): MarketIntelligenceReport => {
  const isGastronomy = briefing.segment.toLowerCase().includes('gastronomia') || briefing.segment.toLowerCase().includes('hambúrguer') || briefing.segment.toLowerCase().includes('restaurante');
  const isRealEstate = briefing.segment.toLowerCase().includes('imobil') || briefing.segment.toLowerCase().includes('corretor') || briefing.segment.toLowerCase().includes('construtora');

  return {
    companyName: briefing.companyName,
    segment: briefing.segment,
    cityState: briefing.cityState || 'Chapecó/SC',
    scoreAudit: Math.floor(Math.random() * 25) + 40, // 40-65 audit score
    marketReachPotential: isRealEstate ? '18.500 buscas mensais de imóveis na região' : '34.200 procuras locais por mês',
    estimatedMonthlyLeads: Math.floor(Math.random() * 40) + 60,
    googleRankOpportunity: 'Posição 1º Lugar com Otimização Rich Snippets & SEO Local',
    commissionSavingsEstimate: isGastronomy ? 'R$ 6.400,00 a R$ 9.800,00/mês economizados em taxas de iFood' : 'R$ 15.000,00+ em aumento de margem direta',
    maturityPhase: briefing.hasWebsite ? 'Crescimento Acelerado' : 'Início Digital',
    recommendedPackage: 'DO OFF AO ON',
    generatedAt: new Date().toLocaleDateString('pt-BR')
  };
};
